// feedback.jsx — mirrors feedback.html exactly
// Two modes: ?role=organiser shows results, default shows submission form
// Star rating with hover + click, anon toggle, categories: content/speaker/organisation/venue/overall
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "https://bmsit-auditorium-api.info-fortifyhub.workers.dev";
const CATS = [
  { key:"content",       label:"Content quality",    desc:"Relevance, depth and quality of topics covered" },
  { key:"speaker",       label:"Speaker / Presenter", desc:"Clarity, knowledge and engagement of the speaker" },
  { key:"organisation",  label:"Organisation",        desc:"Time management, coordination and logistics" },
  { key:"venue",         label:"Venue & facilities",  desc:"Hall, seating, AV equipment and refreshments" },
  { key:"overall",       label:"Overall experience",  desc:"Your overall rating of this event" },
];

function StarRow({ catKey, value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n}
          style={{ fontSize:28, cursor:"pointer", color:(hover||value)>=n?"#f59e0b":"#334155", transition:"color .1s", userSelect:"none" }}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(catKey, n)}
        >★</span>
      ))}
      <span style={{ marginLeft:6, fontSize:12, color:"#94a3b8" }}>{value ? `${value}/5` : "tap to rate"}</span>
    </div>
  );
}

export default function Feedback() {
  const [sp]        = useSearchParams();
  const regID       = sp.get("reg")   || "";
  const emailParam  = sp.get("email") || "";
  const eventID     = sp.get("event") || "";
  const role        = sp.get("role")  || "participant"; // participant | organiser
  const authToken   = sp.get("token") || localStorage.getItem("authToken") || "";

  const [email,    setEmail]    = useState(emailParam);
  const [ratings,  setRatings]  = useState({ content:0, speaker:0, organisation:0, venue:0, overall:0 });
  const [comments, setComments] = useState("");
  const [isAnon,   setIsAnon]   = useState(true);
  const [status,   setStatus]   = useState(null); // {type, msg}
  const [done,     setDone]     = useState(false);
  const [results,  setResults]  = useState(null); // organiser view
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (role === "organiser" && eventID) loadResults();
  }, []); // eslint-disable-line

  const setRating = (key, val) => setRatings(p => ({ ...p, [key]: val }));

  const submitFeedback = async () => {
    if (!email.trim()) { setStatus({ type:"danger", msg:"Email is required." }); return; }
    if (!ratings.overall) { setStatus({ type:"danger", msg:"Please rate the overall experience." }); return; }
    setLoading(true);
    try {
      const d = await fetch(API_URL, { method:"POST", body: JSON.stringify({
        action:"submitFeedback", regID, email: email.trim(), ratings, comments, isAnon
      })}).then(r=>r.json()).catch(()=>null);
      if (d?.success) { setDone(true); }
      else setStatus({ type:"danger", msg: d?.message || "Submission failed. Try again." });
    } finally { setLoading(false); }
  };

  const loadResults = async () => {
    setLoading(true);
    try {
      const url = new URL(API_URL);
      url.searchParams.set("action", "listFeedback");
      url.searchParams.set("bookingID", eventID);
      if (authToken) url.searchParams.set("token", authToken);
      const d = await fetch(url.toString()).then(r=>r.json()).catch(()=>null);
      if (d?.success) setResults(d);
      else setStatus({ type:"danger", msg: d?.message || "Failed to load feedback." });
    } finally { setLoading(false); }
  };

  const S = {
    page:  { fontFamily:"'Inter',system-ui,sans-serif", background:"#0f172a", color:"#f1f5f9", minHeight:"100vh", fontSize:14, padding:"0 0 40px" },
    nav:   { background:"#1e293b", padding:"14px 16px", borderBottom:"1px solid #334155", fontWeight:700 },
    card:  { background:"#1e293b", borderRadius:12, padding:18, margin:"16px 16px 0" },
    label: { fontSize:11, color:"#94a3b8", marginBottom:4, display:"block" },
    inp:   { width:"100%", background:"#0f172a", border:"1px solid #334155", borderRadius:8, padding:"9px 12px", fontSize:13, color:"#f1f5f9", fontFamily:"inherit", boxSizing:"border-box", resize:"vertical" },
    btn:   { background:"#3b82f6", color:"#fff", border:"none", borderRadius:8, padding:"11px", fontSize:13, fontWeight:500, cursor:"pointer", fontFamily:"inherit", width:"100%", marginTop:10 },
    status:(type)=>({ padding:"10px 14px", borderRadius:8, marginTop:10, background:type==="danger"?"rgba(239,68,68,.15)":"rgba(16,185,129,.15)", color:type==="danger"?"#ef4444":"#10b981", fontSize:13 }),
    rrow:  { marginBottom:18 },
    rlabel:{ fontWeight:600, marginBottom:2 },
    rdesc: { fontSize:11, color:"#94a3b8", marginBottom:8 },
  };

  if (done) return (
    <div style={S.page}>
      <div style={S.nav}>Feedback · {eventID}</div>
      <div style={{ ...S.card, textAlign:"center", padding:40 }}>
        <div style={{ fontSize:48, marginBottom:12 }}>🙏</div>
        <div style={{ fontWeight:700, fontSize:18, marginBottom:8 }}>Thank you!</div>
        <div style={{ color:"#94a3b8", marginBottom:20 }}>Your feedback has been recorded. It helps the organiser improve future events.</div>
        <a href={eventID ? `/events/${eventID}` : "/events"} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#3b82f6", color:"#fff", padding:"9px 20px", borderRadius:8, textDecoration:"none", fontSize:13, fontWeight:500 }}>
          ← Back to event
        </a>
      </div>
    </div>
  );

  // Organiser results view
  if (role === "organiser") return (
    <div style={S.page}>
      <div style={S.nav}>📊 Feedback results · {eventID}</div>
      {loading && <div style={{ padding:32, textAlign:"center", color:"#94a3b8" }}>Loading…</div>}
      {status && <div style={{ ...S.status(status.type), margin:"16px 16px 0" }}>{status.msg}</div>}
      {results && (
        <>
          <div style={S.card}>
            <div style={{ fontWeight:600, marginBottom:12 }}>Averages <span style={{ color:"#94a3b8", fontWeight:400, fontSize:12 }}>{results.count} response{results.count!==1?"s":""}</span></div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {Object.entries(results.averages||{}).map(([k,v]) => (
                <div key={k} style={{ background:"#0f172a", borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
                  <div style={{ fontSize:22, fontWeight:800, color:"#f59e0b" }}>{Number(v).toFixed(1)}</div>
                  <div style={{ fontSize:11, color:"#94a3b8", marginTop:2, textTransform:"capitalize" }}>{k}</div>
                </div>
              ))}
            </div>
          </div>
          {(results.feedback||[]).filter(f=>f.comments).length > 0 && (
            <div style={S.card}>
              <div style={{ fontWeight:600, marginBottom:12 }}>Comments</div>
              {(results.feedback||[]).filter(f=>f.comments).map((f,i) => (
                <div key={i} style={{ background:"#0f172a", borderRadius:8, padding:"10px 12px", marginBottom:8, fontSize:13, color:"#94a3b8", fontStyle:"italic", borderLeft:"3px solid #3b82f6" }}>
                  {f.isAnon && <span style={{ fontSize:10, background:"#334155", color:"#94a3b8", padding:"2px 6px", borderRadius:4, marginRight:6 }}>anon</span>}
                  "{f.comments}"
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  // Participant feedback form
  return (
    <div style={S.page}>
      <div style={S.nav}>⭐ Event feedback {eventID && `· ${eventID}`}</div>

      {CATS.map(cat => (
        <div key={cat.key} style={S.card}>
          <div style={S.rrow}>
            <div style={S.rlabel}>{cat.label}</div>
            <div style={S.rdesc}>{cat.desc}</div>
            <StarRow catKey={cat.key} value={ratings[cat.key]} onChange={setRating} />
          </div>
        </div>
      ))}

      <div style={S.card}>
        <label style={S.label}>Your email *</label>
        <input style={{ ...S.inp, marginBottom:14 }} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" />

        <label style={S.label}>Comments & suggestions (optional)</label>
        <textarea style={{ ...S.inp, minHeight:90, marginBottom:14 }} rows={4} value={comments} onChange={e=>setComments(e.target.value)} placeholder="Share your thoughts, what you liked, what could improve…" />

        <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontSize:13, color:"#94a3b8" }}>
          <input type="checkbox" checked={isAnon} onChange={e=>setIsAnon(e.target.checked)} style={{ width:16, height:16 }} />
          Submit anonymously
        </label>

        {status && <div style={S.status(status.type)}>{status.msg}</div>}
        <button style={S.btn} onClick={submitFeedback} disabled={loading}>
          {loading ? "Submitting…" : "✉ Submit feedback"}
        </button>
      </div>
    </div>
  );
}
